/**
 * Audio Manager "Repair" — relink missing audios and transcripts.
 *
 * Two modes, switched at the top of the dialog:
 *  - This day:    previews the day open in the Audio Manager straight away.
 *  - Repair full: asks the server for EVERY day with a visit or a storage
 *                 folder (list_days), then previews them in batches of
 *                 BATCH_DAYS, CONCURRENCY batches at a time, with progress and
 *                 Stop; fixes stream in as days finish.
 *
 * Either way it's a PREVIEW from POST /api/transcription-backup/repair-links/
 * (nothing written): every proposed fix, grouped by day and patient, with its
 * evidence, a player and the transcript text being added. Sure fixes start
 * ticked, likely ones unticked; cases the server won't guess at are listed as
 * "Needs a person". Apply sends only the ticked ids, grouped by day; the
 * server re-checks each against the live rows and skips anything that changed.
 *
 * "Needs a person" notes list the files they're about: listen to each, move it
 * to any patient (the Bucket view's search-any-patient picker + move preview),
 * and where several files could fix a chart entry, tick the one to use — that
 * tick is an apply id like any other fix.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Collapse,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Spinner,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { supabasePublic } from "../../components/supabaseClient";
import { getBackendURL } from "../../components/env";
import AudioPlayer from "../../components/table/audio/AudioPlayer";
import { clearBackupCache } from "../../components/AudioManagerModal";
import { MoveDialog, TargetPicker, dayFolderOf } from "./AudioBucketView";

const MAX_DAYS = 31; // server limit per request
export const BATCH_DAYS = 5;
export const CONCURRENCY = 3;

const TYPE_LABEL = {
  relink_audio: "Relink missing audio",
  link_file: "Add file to chart",
  link_transcript_audio: "Add transcript's recording",
  fix_transcript_url: "Point transcript at its audio",
  link_transcript: "Add transcript from backup",
};

const leafOf = (u) => {
  try {
    return decodeURIComponent(String(u || "").split("?")[0].split("/").pop());
  } catch {
    return String(u || "").split("/").pop();
  }
};

/** One line saying what the fix does, in plain words. */
export function describeAction(a) {
  switch (a.type) {
    case "relink_audio":
      return `“${a.label}” points at a file that no longer exists → link it to ${leafOf(a.new_url)}`;
    case "link_file":
      return `${leafOf(a.url)} is in storage but on no chart → add it to ${a.patient_name}'s audios`;
    case "link_transcript_audio":
      return a.old_url
        ? `A transcript's recording ${leafOf(a.old_url)} is gone → add the copy ${leafOf(a.url)} to the chart`
        : `A transcript's recording ${leafOf(a.url)} isn't on the chart → add it`;
    case "fix_transcript_url":
      return `A transcript points at missing ${leafOf(a.old_url)} → point it at “${a.label || leafOf(a.new_url)}”`;
    case "link_transcript": {
      const audio = leafOf(a.audio_path || a.url);
      const name = a.label && a.label !== audio ? `“${a.label}” (${audio})` : `“${audio}”`;
      return `Audio ${name} has no transcript → attach the one saved in ${leafOf(a.json_path)}`;
    }
    default:
      return a.type;
  }
}

/** Files to listen to before applying: [label, url]. */
const playersOf = (a) => {
  if (a.type === "link_transcript") return a.url ? [["The audio this transcript attaches to", a.url]] : [];
  if (a.type === "fix_transcript_url") return [];
  const u = a.new_url || a.url;
  return u ? [[null, u]] : [];
};

async function callRepair(body) {
  const { data: { session } = {} } = await supabasePublic().auth.getSession();
  const resp = await fetch(`${getBackendURL()}/api/transcription-backup/repair-links/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  const result = await resp.json().catch(() => ({}));
  if (result.error === "doctor_role_required") throw new Error("Doctor role required to repair recordings.");
  if (!resp.ok || !result.success) throw new Error(result.error || `Repair failed (${resp.status})`);
  return result;
}

/** Split into chunks of `size`. */
export const chunk = (list, size) => {
  const out = [];
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
  return out;
};

function ActionRow({ a, checked, onToggle }) {
  const [listen, setListen] = useState(false);
  const players = playersOf(a);
  return (
    <Box borderWidth="1px" borderRadius="md" p={2} bg={checked ? "green.50" : undefined}>
      <Flex gap={2} align="flex-start">
        <Checkbox mt={1} isChecked={checked} onChange={(e) => onToggle(e.target.checked)} />
        <Box flex="1" minW={0}>
          <HStack spacing={2} wrap="wrap" mb={1}>
            <Badge colorScheme="purple">{TYPE_LABEL[a.type] || a.type}</Badge>
            <Badge colorScheme={a.confidence === "high" ? "green" : "yellow"} title={a.reason}>
              {a.confidence === "high" ? "sure" : "likely"}
            </Badge>
          </HStack>
          <Text fontSize="sm" wordBreak="break-word">{describeAction(a)}</Text>
          <Text fontSize="xs" color="gray.500">Why: {a.reason}</Text>
          {a.type === "link_transcript" && (
            <Text fontSize="xs" color="gray.600" wordBreak="break-all">
              Attaches to audio: <b>{a.audio_path || leafOf(a.url)}</b>
              <br />
              From backup: transcription-backups/{a.json_path}
            </Text>
          )}
          {(a.old_path || a.new_path) && (
            <Text fontSize="xs" color="gray.500" wordBreak="break-all">
              {a.old_path ? `${a.old_path} → ` : ""}
              {a.new_path}
            </Text>
          )}
          {a.preview && (
            <Box mt={1} p={2} bg="gray.50" borderRadius="md" maxH="120px" overflowY="auto">
              <Text fontSize="xs" whiteSpace="pre-wrap" color="gray.700">
                {a.preview}
                {a.preview.length >= 300 ? "…" : ""}
              </Text>
            </Box>
          )}
          {players.length > 0 && (
            <Box mt={1}>
              <Button size="xs" variant="link" onClick={() => setListen((v) => !v)}>
                {listen ? "Hide player" : players.length > 1 ? "Listen to both first" : "Listen first"}
              </Button>
              <Collapse in={listen} unmountOnExit>
                <VStack align="stretch" spacing={1} mt={1}>
                  {players.map(([label, u]) => (
                    <Box key={u}>
                      {label && <Text fontSize="xs" fontWeight="semibold">{label}</Text>}
                      <Flex justify="center" overflowX="auto">
                        <AudioPlayer audioUrl={u} audioDisplayName={leafOf(u)} />
                      </Flex>
                    </Box>
                  ))}
                </VStack>
              </Collapse>
            </Box>
          )}
        </Box>
      </Flex>
    </Box>
  );
}

const fmtSize = (n) => {
  const b = Number(n) || 0;
  if (!b) return "";
  return b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;
};

/** A note's stored file as the item the Bucket view's picker / move dialog take. */
export const noteFileItem = (f) => ({
  key: `${f.bucket}/${f.path}`,
  type: f.bucket === "transcription-backups" ? "json" : "audio",
  bucket: f.bucket,
  path: f.path,
  name: f.name,
  url: f.url,
  size: f.size || 0,
  created_at: f.created_at || null,
  linkedRow: null, // the server finds the chart that lists it, if any
  fileOwner: null, // the picker searches by the filename instead
  folderDate: dayFolderOf(String(f.path || "").split("/"))?.date || null,
});

/** One file a "Needs a person" note is about: listen / read, use it, move it. */
function NoteFile({ f, checked, onToggle, movedTo, onMove }) {
  const [open, setOpen] = useState(false);
  const isJson = f.bucket === "transcription-backups";
  return (
    <Box borderWidth="1px" borderRadius="md" p={2} mt={1} bg={movedTo ? "gray.50" : checked ? "green.50" : "white"}>
      <Flex align="center" gap={2} wrap="wrap">
        {f.option && !movedTo && (
          <Checkbox isChecked={checked} onChange={(e) => onToggle(e.target.checked)}>
            <Text fontSize="xs">{isJson ? "Use this transcript" : "Use this file"}</Text>
          </Checkbox>
        )}
        <Text fontSize="xs">{isJson ? "📄" : "🎵"}</Text>
        <Text fontSize="sm" wordBreak="break-all" flex="1" minW="200px">{f.name}</Text>
        <Badge variant="outline" textTransform="none" fontSize="9px">{f.bucket}</Badge>
        {f.size && <Text fontSize="xs" color="gray.500">{fmtSize(f.size)}</Text>}
        {movedTo ? (
          <Badge colorScheme="green" textTransform="none">moved to {movedTo}</Badge>
        ) : (
          <>
            <Button size="xs" variant="ghost" onClick={() => setOpen((v) => !v)}>
              {open ? "Hide" : isJson ? "Read" : "Listen"}
            </Button>
            <Button size="xs" colorScheme="purple" variant="outline" onClick={onMove}>Move to…</Button>
          </>
        )}
      </Flex>
      <Text fontSize="xs" color="gray.500" wordBreak="break-all">{f.path}</Text>
      {isJson && f.option?.audio_path && (
        <Text fontSize="xs" color="gray.600" wordBreak="break-all">
          Would attach to audio: <b>{f.option.audio_path}</b>
        </Text>
      )}
      <Collapse in={open && !movedTo} unmountOnExit>
        <Box mt={1}>
          {isJson ? (
            <Text fontSize="xs" whiteSpace="pre-wrap" bg="gray.50" p={2} borderRadius="md" maxH="140px" overflowY="auto">
              {f.preview || "No transcript text."}
            </Text>
          ) : (
            <Flex justify="center" overflowX="auto">
              <AudioPlayer audioUrl={f.url} audioDisplayName={f.name} />
            </Flex>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

export default function RepairDialog({ isOpen, onClose, date, schemas, onDone, dayRows = [] }) {
  const toast = useToast();
  const [mode, setMode] = useState("day"); // "day" | "full"
  const [plan, setPlan] = useState(null); // { actions, notes }
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [checked, setChecked] = useState({});
  // "Needs a person" moves: picker open for a file, its move preview, and done moves.
  const [picker, setPicker] = useState(null); // { item, note }
  const [move, setMove] = useState(null); // { items, target, note, file }
  const [moved, setMoved] = useState({}); // file key -> patient name
  // Full scan: the days to check (with their schemas) and how far it got.
  const [progress, setProgress] = useState(null); // { done, total, listing }
  const daySchemas = useRef(new Map()); // date -> schemas planned for it
  const stopRef = useRef(false);
  const runRef = useRef(0);

  const addResults = (result) => {
    setPlan((p) => ({
      actions: [...(p?.actions || []), ...(result.actions || [])],
      notes: [...(p?.notes || []), ...(result.notes || [])],
    }));
    setChecked((c) => ({
      ...c,
      ...Object.fromEntries((result.actions || []).map((a) => [a.id, Boolean(a.default_selected)])),
    }));
  };

  const reset = () => {
    setPlan(null);
    setChecked({});
    setError("");
    setProgress(null);
    daySchemas.current = new Map();
  };

  const previewDay = useCallback(async () => {
    const run = ++runRef.current;
    reset();
    setLoading(true);
    try {
      daySchemas.current.set(date, schemas);
      const result = await callRepair({ days: [{ date, schemas }], schemas });
      if (run === runRef.current) addResults(result);
    } catch (err) {
      if (run === runRef.current) setError(err.message);
    } finally {
      if (run === runRef.current) setLoading(false);
    }
  }, [date, schemas]);

  const runFull = async () => {
    const run = ++runRef.current;
    reset();
    stopRef.current = false;
    setLoading(true);
    setProgress({ done: 0, total: 0, listing: true });
    try {
      const { days = [] } = await callRepair({ list_days: true, schemas });
      if (run !== runRef.current) return;
      for (const d of days) daySchemas.current.set(d.date, d.schemas);
      setPlan({ actions: [], notes: [] });
      setProgress({ done: 0, total: days.length, listing: false });
      const batches = chunk(days, BATCH_DAYS);
      let next = 0;
      let failed = 0;
      const worker = async () => {
        while (next < batches.length && !stopRef.current && run === runRef.current) {
          const batch = batches[next++];
          try {
            // eslint-disable-next-line no-await-in-loop
            const result = await callRepair({
              days: batch.map((d) => ({ date: d.date, schemas: d.schemas })),
              schemas,
            });
            if (run === runRef.current) addResults(result);
          } catch {
            failed += batch.length;
          }
          if (run === runRef.current) setProgress((p) => ({ ...p, done: p.done + batch.length }));
        }
      };
      await Promise.all(Array.from({ length: CONCURRENCY }, worker));
      if (failed && run === runRef.current) setError(`${failed} day(s) couldn't be checked — run Repair full again for them.`);
    } catch (err) {
      if (run === runRef.current) setError(err.message);
    } finally {
      if (run === runRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && mode === "day" && date && schemas?.length) previewDay();
  }, [isOpen, mode, previewDay, date, schemas]);
  // Closing the dialog stops a running scan.
  useEffect(() => () => {
    stopRef.current = true;
    runRef.current += 1;
  }, []);

  const switchMode = (m) => {
    if (m === mode) return;
    stopRef.current = true;
    runRef.current += 1;
    reset();
    setLoading(false);
    setMode(m);
  };

  const groups = useMemo(() => {
    const m = new Map();
    for (const a of plan?.actions || []) {
      const day = a.date || date;
      const k = `${day}::${a.schema}::${a.row_id}`;
      if (!m.has(k)) {
        m.set(k, { key: k, date: day, row_id: a.row_id, patient_name: a.patient_name, EHR_ID: a.EHR_ID, schema: a.schema, actions: [] });
      }
      m.get(k).actions.push(a);
    }
    return [...m.values()].sort((x, y) => String(y.date).localeCompare(String(x.date)));
  }, [plan, date]);
  const ids = Object.keys(checked).filter((id) => checked[id]);
  const full = mode === "full";
  const scanning = full && loading;

  const apply = async () => {
    setApplying(true);
    try {
      // Ticked ids by day; each request covers at most MAX_DAYS days.
      const byDay = new Map();
      // Fixes plus the options a person ticked inside "Needs a person" notes.
      const options = plan.notes.flatMap((n) =>
        (n.files || []).filter((f) => f.option).map((f) => ({ ...f.option, date: f.option.date || n.date })),
      );
      for (const a of [...plan.actions, ...options]) {
        if (!checked[a.id]) continue;
        const day = a.date || date;
        if (!byDay.has(day)) byDay.set(day, []);
        byDay.get(day).push(a.id);
      }
      const total = { applied: [], skipped: [], stale: [], errors: [] };
      for (const daysChunk of chunk([...byDay.keys()], MAX_DAYS)) {
        // eslint-disable-next-line no-await-in-loop
        const result = await callRepair({
          days: daysChunk.map((d) => ({ date: d, schemas: daySchemas.current.get(d) || schemas })),
          schemas,
          apply_ids: daysChunk.flatMap((d) => byDay.get(d)),
        });
        total.applied.push(...result.applied);
        total.skipped.push(...result.skipped);
        total.stale.push(...result.stale);
        total.errors.push(...result.errors);
      }
      for (const g of groups) clearBackupCache(g.row_id);
      const problems = [
        ...total.skipped.map((s) => s.reason),
        ...(total.stale.length ? [`${total.stale.length} changed since the preview — run Repair again`] : []),
        ...total.errors.map((e) => e.error),
      ];
      toast({
        title: `Repaired ${total.applied.length} of ${ids.length}`,
        description: problems.length ? [...new Set(problems)].join("\n") : undefined,
        status: problems.length ? "warning" : "success",
        duration: 8000,
        isClosable: true,
      });
      onDone();
      onClose();
    } catch (err) {
      toast({ title: "Repair failed", description: err.message, status: "error", duration: 8000, isClosable: true });
    } finally {
      setApplying(false);
    }
  };

  const dayCount = new Set(groups.map((g) => g.date)).size;

  return (
    <Modal isOpen={isOpen} onClose={applying ? () => {} : onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex align="center" gap={3} wrap="wrap">
            <Text>Repair recordings</Text>
            <ButtonGroup size="sm" isAttached variant="outline" colorScheme="orange" isDisabled={applying}>
              <Button variant={full ? "outline" : "solid"} onClick={() => switchMode("day")}>
                This day ({date})
              </Button>
              <Button variant={full ? "solid" : "outline"} onClick={() => switchMode("full")}>
                Repair full
              </Button>
            </ButtonGroup>
          </Flex>
          <Text fontSize="sm" fontWeight="normal" color="gray.500">
            {full ? "Every day with a visit or stored files" : date} ·{" "}
            {schemas?.length === 1 ? schemas[0] : `${schemas?.length || 0} providers`} · nothing changes until you apply
          </Text>
        </ModalHeader>
        {!applying && <ModalCloseButton />}
        <ModalBody>
          {full && !progress && !plan && (
            <Box borderWidth="1px" borderRadius="md" p={4}>
              <Text mb={2}>
                Repair full checks <b>every day</b> — not only {date} — for missing files, files on no chart and lost
                transcripts, across {schemas?.length === 1 ? schemas[0] : `all ${schemas?.length || 0} providers`}.
              </Text>
              <Text fontSize="sm" color="gray.500" mb={3}>
                It only reads while scanning; fixes appear as days finish, and nothing changes until you apply.
                A few minutes for a large history.
              </Text>
              <Button colorScheme="orange" onClick={runFull}>Start full scan</Button>
            </Box>
          )}
          {progress && (
            <Box mb={3}>
              <Flex align="center" gap={2} mb={1}>
                {scanning && <Spinner size="sm" />}
                <Text fontSize="sm">
                  {progress.listing
                    ? "Finding every day with visits or stored files…"
                    : `${scanning ? "Checked" : "Finished:"} ${progress.done} of ${progress.total} days · ${plan?.actions.length || 0} fixes found`}
                </Text>
                <Box flex="1" />
                {scanning && !progress.listing && (
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={() => {
                      stopRef.current = true;
                    }}
                  >
                    Stop
                  </Button>
                )}
              </Flex>
              {!progress.listing && progress.total > 0 && (
                <Progress size="sm" colorScheme="orange" value={(100 * progress.done) / progress.total} borderRadius="md" />
              )}
            </Box>
          )}
          {!full && loading && (
            <Flex align="center" gap={2} py={8} justify="center">
              <Spinner />
              <Text>Checking every chart, file and transcript backup for {date}…</Text>
            </Flex>
          )}
          {error && (
            <Alert status="error" borderRadius="md" mb={3}>
              <AlertIcon />
              {error}
            </Alert>
          )}
          {plan && !(full && progress?.listing) && (
            <VStack align="stretch" spacing={4}>
              {groups.length === 0 ? (
                !scanning && (
                  <Alert status="success" borderRadius="md">
                    <AlertIcon />
                    Nothing to relink — every recording and transcript {full ? "on every day" : "for this day"} is where it
                    should be.
                  </Alert>
                )
              ) : (
                <Flex align="center" gap={2}>
                  <Text fontSize="sm">
                    {plan.actions.length} fix{plan.actions.length === 1 ? "" : "es"} for {groups.length} patient
                    {groups.length === 1 ? "" : "s"}
                    {full ? ` on ${dayCount} day${dayCount === 1 ? "" : "s"}` : ""}. “Sure” ones are ticked; review the
                    “likely” ones.
                  </Text>
                  <Box flex="1" />
                  <Button size="xs" variant="ghost" onClick={() => setChecked(Object.fromEntries(plan.actions.map((a) => [a.id, true])))}>
                    Tick all
                  </Button>
                  <Button size="xs" variant="ghost" onClick={() => setChecked({})}>Untick all</Button>
                </Flex>
              )}
              {groups.map((g) => (
                <Box key={g.key}>
                  <HStack mb={2}>
                    {full && <Badge colorScheme="purple">{g.date}</Badge>}
                    <Text fontWeight="semibold">{g.patient_name || "Unnamed patient"}</Text>
                    {g.EHR_ID && <Text fontSize="xs" color="gray.500">{g.EHR_ID}</Text>}
                    <Badge colorScheme="teal" variant="outline" textTransform="none">{g.schema}</Badge>
                  </HStack>
                  <VStack align="stretch" spacing={2}>
                    {g.actions.map((a) => (
                      <ActionRow
                        key={a.id}
                        a={a}
                        checked={Boolean(checked[a.id])}
                        onToggle={(on) => setChecked((c) => ({ ...c, [a.id]: on }))}
                      />
                    ))}
                  </VStack>
                </Box>
              ))}
              {plan.notes.length > 0 && (
                <Box borderWidth="1px" borderColor="orange.200" borderRadius="md" p={3}>
                  <Text fontWeight="semibold" mb={1}>Needs a person ({plan.notes.length})</Text>
                  <Text fontSize="xs" color="gray.500" mb={2}>
                    Not repaired automatically — no evidence, or more than one possible match. Listen to each file
                    and move it to the right patient (search any provider or date), or tick the one to use.
                  </Text>
                  <VStack align="stretch" spacing={1}>
                    {plan.notes.map((n, i) => (
                      <Box key={i} fontSize="sm">
                        <Text>
                          {full && n.date ? <Badge mr={1} colorScheme="purple">{n.date}</Badge> : null}
                          {n.patient_name ? <b>{n.patient_name}: </b> : null}
                          {n.text}
                        </Text>
                        {(n.files || []).map((f) => (
                          <NoteFile
                            key={`${f.bucket}/${f.path}`}
                            f={f}
                            checked={Boolean(f.option && checked[f.option.id])}
                            onToggle={(on) => setChecked((c) => ({ ...c, [f.option.id]: on }))}
                            movedTo={moved[`${f.bucket}/${f.path}`]}
                            onMove={() => setPicker({ item: noteFileItem(f), file: f })}
                          />
                        ))}
                      </Box>
                    ))}
                  </VStack>
                </Box>
              )}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter gap={2}>
          <Button
            variant="ghost"
            onClick={full ? runFull : previewDay}
            isDisabled={loading || applying || (full && !progress)}
          >
            {full ? "Scan again" : "Re-check"}
          </Button>
          <Button variant="ghost" onClick={onClose} isDisabled={applying}>Cancel</Button>
          <Button colorScheme="green" onClick={apply} isLoading={applying} isDisabled={!plan || ids.length === 0 || scanning}>
            Apply {ids.length} fix{ids.length === 1 ? "" : "es"}
          </Button>
        </ModalFooter>
      </ModalContent>
      {picker && (
        <TargetPicker
          items={[picker.item]}
          dayRows={dayRows}
          schemas={schemas}
          date={date}
          onClose={() => setPicker(null)}
          onPick={(row) => {
            setMove({ items: [picker.item], target: row, file: picker.file });
            setPicker(null);
          }}
        />
      )}
      {move && (
        <MoveDialog
          move={move}
          onClose={() => setMove(null)}
          onDone={() => {
            const key = `${move.file.bucket}/${move.file.path}`;
            setMoved((m) => ({ ...m, [key]: move.target.patient_name }));
            // A moved file can't also be "used" for the note's fix.
            if (move.file.option) setChecked((c) => ({ ...c, [move.file.option.id]: false }));
            setMove(null);
            onDone();
          }}
        />
      )}
    </Modal>
  );
}
